import { useState, useEffect, useCallback } from 'react';

/**
 * A hook that persists state to localStorage
 * @param key - The localStorage key to use
 * @param defaultValue - The default value if no stored value exists
 * @returns [value, setValue] tuple like useState
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize state from localStorage or default
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored) as T;
      }
    } catch (error) {
      console.error(`Failed to load persisted state for ${key}:`, error);
    }
    return defaultValue;
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Failed to save persisted state for ${key}:`, error);
    }
  }, [key, state]);

  // Wrapper to handle both direct values and updater functions
  const setPersistedState = useCallback((value: T | ((prev: T) => T)) => {
    setState(value);
  }, []);

  return [state, setPersistedState];
}

/**
 * A hook that persists multiple state values as an object
 * @param key - The localStorage key to use
 * @param defaultValues - Object with default values for each field
 * @returns [values, setValues, updateValue] tuple
 */
export function usePersistedCalculatorState<T extends Record<string, unknown>>(
  key: string,
  defaultValues: T
): [T, (values: T) => void, <K extends keyof T>(field: K, value: T[K]) => void] {
  const [state, setState] = usePersistedState<T>(key, defaultValues);

  const updateValue = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setState((prev) => ({ ...prev, [field]: value }));
  }, [setState]);

  return [state, setState, updateValue];
}

export default usePersistedState;
